import { useEffect, useState } from "react";
import { RefreshCw, Loader2, HeartPulse, CheckCircle2, AlertTriangle, XOctagon, Clock } from "lucide-react";
import api from "@/lib/api";

/**
 * Painel de saúde do sistema — expõe o boot report + botão para correr
 * o deep-health on-demand. Renderiza uma linha por subsistema com
 * status/duração. Só é montado quando o utilizador é admin
 * (guardado pela página host).
 *
 * data-testid: system-health-card
 */
const STATUS_META = {
  ok:       { label: "OK",       cls: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25", Icon: CheckCircle2 },
  skipped:  { label: "N/A",      cls: "text-muted-foreground bg-muted/40 border-border",        Icon: Clock },
  fail:     { label: "FALHA",    cls: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25",              Icon: XOctagon },
  error:    { label: "ERRO",     cls: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25",              Icon: XOctagon },
  timeout:  { label: "TIMEOUT",  cls: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25",        Icon: AlertTriangle },
};

const OVERALL_META = {
  ok:       { label: "Tudo OK",     cls: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))]/40 text-[hsl(var(--success))]" },
  degraded: { label: "Degradado",   cls: "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))]/40 text-[hsl(var(--warning))]" },
  critical: { label: "Crítico",     cls: "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))]/40 text-[hsl(var(--danger))]" },
};

const CHECK_LABEL = {
  env_vars: "Variáveis de ambiente",
  mongo: "MongoDB (Atlas)",
  emergent_llm: "Emergent LLM SDK",
  zoho: "Zoho CRM (config)",
  office365: "Office 365",
  clickup: "ClickUp",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("pt-PT", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export default function SystemHealthCard() {
  const [bootReport, setBootReport] = useState(null);
  const [bootAt, setBootAt] = useState(null);
  const [bootId, setBootId] = useState(null);
  const [deep, setDeep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const loadBoot = async () => {
    try {
      const { data } = await api.get("/system/boot-report");
      setBootReport(data.last_boot_report || null);
      setBootAt(data.last_boot_report_at || null);
      setBootId(data.boot_id || null);
    } catch (e) {
      // silent — non-admins hit 403
    } finally {
      setLoading(false);
    }
  };

  const runDeep = async () => {
    setRunning(true);
    try {
      const { data } = await api.get("/system/deep-health");
      setDeep(data);
    } catch (e) {
      // If we hit 503 the endpoint STILL returns the report body — axios
      // treats it as an error, so unwrap it.
      if (e?.response?.data) setDeep(e.response.data);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { loadBoot(); }, []);

  const report = deep || bootReport;
  const overall = report?.overall || (loading ? "loading" : "unknown");
  const meta = OVERALL_META[overall] || { label: "Desconhecido", cls: "border-border bg-muted/40 text-foreground" };

  return (
    <div
      data-testid="system-health-card"
      className={`rounded-xl border-2 p-5 ${meta.cls}`}
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center shadow-sm">
            <HeartPulse className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-70">
              Sensor de Saúde · Cloudflare 520 alarme
            </div>
            <div className="font-display text-2xl tracking-tight leading-tight">
              {loading ? "A carregar…" : meta.label}
            </div>
            <div className="text-xs opacity-70 mt-1">
              Último boot: {fmt(bootAt)} · <span className="font-mono">{bootId || "—"}</span>
              {report?.elapsed_ms != null && <span> · {report.elapsed_ms}ms</span>}
            </div>
          </div>
        </div>
        <button
          onClick={runDeep}
          disabled={running || loading}
          data-testid="run-deep-health-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-card/80 hover:bg-card text-foreground px-3 py-1.5 text-xs font-semibold shadow-sm disabled:opacity-50"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Correr deep-health
        </button>
      </div>

      {report?.checks && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="system-health-grid">
          {report.checks.map(c => {
            const st = STATUS_META[c.status] || STATUS_META.error;
            const Icon = st.Icon;
            const detail = c.detail || {};
            const detailText = detail.error || detail.reason || detail.missing_required?.join(", ") || "";
            return (
              <div key={c.check}
                   data-testid={`system-check-${c.check}`}
                   className={`rounded-lg border ${st.cls} px-3 py-2`}>
                <div className="flex items-center justify-between text-[11px] uppercase tracking-widest font-semibold">
                  <span className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" /> {st.label}
                  </span>
                  <span className="tabular-nums opacity-70">{c.elapsed_ms}ms</span>
                </div>
                <div className="text-sm font-semibold mt-0.5">
                  {CHECK_LABEL[c.check] || c.check}
                </div>
                {detailText && (
                  <div className="text-[11px] opacity-70 mt-0.5 truncate" title={detailText}>
                    {detailText}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
